import React from "react";
import { useT } from "@/lib/LanguageContext";
import { actionDetail, actionOutcome, isLegacyAction } from "./runOutcome";
import { actionLabel } from "./automationCodes";

const OUTCOME_STYLE = {
  applied: "bg-emerald-50 text-emerald-700 border-emerald-200",
  simulated: "bg-slate-100 text-slate-600 border-slate-200",
  skipped: "bg-amber-50 text-amber-700 border-amber-200",
  failed: "bg-rose-50 text-rose-700 border-rose-200",
};

/**
 * One line per action of a run: the action, what happened to it (or, for a dry run, what
 * would happen) and the reason. `dry` switches the outcome wording to "would ..."; a legacy
 * row is labelled as such so an old "simulated" is not mistaken for a new one.
 */
export default function RunOutcomeList({ results, dry = false, testId = "run-outcomes" }) {
  const t = useT("superadmin");
  const rows = Array.isArray(results) ? results : [];
  if (rows.length === 0) {
    return <p className="text-xs text-slate-500" data-testid={`${testId}-empty`}>{t("hauto.noActionResults")}</p>;
  }
  return (
    <ul className="space-y-1.5" data-testid={testId}>
      {rows.map((res, i) => {
        const outcome = actionOutcome(res);
        const detail = actionDetail(res, t, { dry });
        return (
          <li key={`${res.type}-${i}`} className="flex items-start gap-2 text-xs" data-testid={`${testId}-item-${i}`}>
            <span
              className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-medium ${OUTCOME_STYLE[outcome]}`}
              data-testid={`${testId}-outcome-${i}`}
              data-outcome={outcome}
            >
              {t(dry ? `hauto.outcomeDry.${outcome}` : `hauto.outcome.${outcome}`)}
              {!dry && isLegacyAction(res) ? ` · ${t("hauto.legacy")}` : ""}
            </span>
            <span className="min-w-0">
              <span className="font-medium text-slate-700">{actionLabel(t, res.type)}</span>
              {detail && <span className="text-slate-500"> · {detail}</span>}
              {res.error && <span className="block text-rose-600 break-words">{String(res.error)}</span>}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
